import React from "react";
import { ChevronRight, Timer } from "lucide-react";

import { formatCurrency, formatInt, formatPct } from "@/features/relatorio-vendas/lib/formato";

import { Variacao } from "../componentes/Base";
import IconeModulo from "../componentes/IconeModulo";
import LinhaDoTempo from "../componentes/LinhaDoTempo";
import MapaHoras from "../componentes/MapaHoras";
import { abaPorChave } from "../modulos";
import { META_MINUTOS, ateMeta, formatarDecimal, formatarMinutos, variacao } from "../regras";

// O que o número grande de cada cartão quer dizer (uma linha só em todos)
const TEMPO = {
  mesas: "Tempo médio de mesa",
  comandas: "Tempo médio aberta",
  delivery: "Até entregar",
  retirada: "Até o cliente buscar",
  ifood: "Até entregar",
};

// Uma linha "rótulo ........ valor": cabe no cartão estreito sem cortar o número
function Metrica({ rotulo, valor }) {
  return (
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <span className="text-[11px] text-muted-foreground truncate">{rotulo}</span>
      <span className="text-sm font-bold tabular-nums whitespace-nowrap">{valor}</span>
    </div>
  );
}

// As 4 linhas de baixo: as mesmas posições em todo cartão; a 3ª e a 4ª mudam com o módulo
function metricas(m) {
  const vendas = { mesas: "Ocupações", comandas: "Comandas" }[m.key] || "Pedidos";
  const base = [[vendas, formatInt(m.vendas)], ["Ticket médio", formatCurrency(m.ticket_medio)]];
  const porHora = ["R$ por hora", m.faturamento_hora != null ? formatCurrency(m.faturamento_hora) : "—"];
  if (m.key === "mesas") return [...base, ["Pessoas por mesa", m.pessoas_media != null ? formatarDecimal(m.pessoas_media) : "—"], porHora];
  if (m.key === "comandas") return [...base, ["Itens por venda", formatarDecimal(m.itens_media)], porHora];
  const meta = META_MINUTOS[m.key];
  const pct = ateMeta(m.faixas, meta);
  return [...base, ["Itens por venda", formatarDecimal(m.itens_media)], [`Até ${meta} min`, pct == null ? "—" : formatPct(pct, 0)]];
}

function Cartao({ m, antes, aoAbrir }) {
  const aba = abaPorChave(m.key);
  const parado = !m.vendas;
  // Tempo menor é melhor só nas entregas; na mesa e na comanda, ficar mais não é bom nem ruim
  const melhorTempo = m.etapas ? "menos" : null;
  return (
    <button type="button" onClick={aoAbrir} data-e2e={`cartao-${m.key}`}
      className={`group relative overflow-hidden text-left bg-card rounded-2xl border p-4 ring-1 ${aba.tema.anel} transition-shadow ${parado ? "opacity-60" : "hover:shadow-lg"}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${aba.tema.fundo} to-transparent pointer-events-none`} />
      <div className="relative">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <IconeModulo modulo={aba} tamanho="w-9 h-9" forma="rounded-xl" icone="w-5 h-5" />
            <h3 className="font-bold text-base truncate">{aba.nome}</h3>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider truncate">{TEMPO[m.key]}</p>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-black tabular-nums leading-tight">{parado ? "—" : formatarMinutos(m.tempo_medio)}</p>
          {!parado && <Variacao v={variacao(m.tempo_medio, antes?.tempo_medio, { tipo: "min", melhor: melhorTempo })}
            antes={antes?.tempo_medio != null ? formatarMinutos(antes.tempo_medio) : null} />}
        </div>
        {/* Mesma altura em todo cartão: a barra das etapas só existe nas entregas */}
        <div className="h-2.5 mt-2">{m.etapas && !parado && <LinhaDoTempo etapas={m.etapas} compacta />}</div>
        <div className="mt-3 pt-3 border-t border-border/60">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Faturamento</p>
            <Variacao v={variacao(m.faturamento, antes?.faturamento)} antes={antes ? formatCurrency(antes.faturamento) : null} />
          </div>
          <p className="text-lg font-black tabular-nums leading-tight truncate">{formatCurrency(m.faturamento)}</p>
        </div>
        {parado
          ? <p className="text-xs text-muted-foreground italic mt-3">Sem vendas no período.</p>
          : (
            <div className="space-y-1 mt-2.5">
              {metricas(m).map(([rotulo, valor]) => <Metrica key={rotulo} rotulo={rotulo} valor={valor} />)}
            </div>
          )}
      </div>
    </button>
  );
}

// Resumo: um cartão por módulo (toque abre a aba) e o faturamento hora a hora de todos.
export default function ResumoAba({ dados, aoAbrirAba }) {
  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Timer className="w-4 h-4" /> Toque num módulo para ver os detalhes.
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-e2e="cartoes-resumo">
        {(dados.modulos || []).map((m) => <Cartao key={m.key} m={m} antes={dados.anterior?.modulos?.[m.key]} aoAbrir={() => aoAbrirAba(m.key)} />)}
      </div>
      <MapaHoras dados={dados.por_hora} />
    </>
  );
}
